import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Save, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AIProfileReader } from "@/components/legal/AIProfileReader";

const EMPTY_BANK = { banco: "", clabe: "", cuenta: "", titular: "" };
const EMPTY_SOCIO = { nombre: "", acciones: "", monto: "", porcentaje: "" };

export default function CorporateProfile() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/legal/profile").then((r) => setForm(r.data)).catch(() => toast.error("No se pudo cargar el perfil"));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setRow = (listKey, i, k, v) => setForm((f) => {
    const rows = [...(f[listKey] || [])];
    rows[i] = { ...rows[i], [k]: v };
    return { ...f, [listKey]: rows };
  });
  const delRow = (listKey, i) => set(listKey, (form[listKey] || []).filter((_, j) => j !== i));

  const applyAI = (result) => {
    setForm((f) => {
      const next = { ...f };
      Object.entries(result.fields || {}).forEach(([k, v]) => { if (v) next[k] = v; });
      if ((result.actividades_economicas || []).length) next.actividades_economicas = result.actividades_economicas;
      if ((result.socios || []).length) next.socios = result.socios;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { id, updated_at, updated_by, ...payload } = form;
      await api.put("/legal/profile", payload);
      toast.success("Expediente corporativo guardado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="p-8 text-center text-slate-400">Cargando…</div>;

  const field = (label, k, type = "text") => (
    <div key={k}>
      <label className="label-mono block mb-1">{label}</label>
      <input type={type} data-testid={`legal-profile-${k}`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)}
        className="w-full input-cyber" />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="legal-profile-tab">
      <div className="panel p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-cyan-300">Lector IA de documentos</div>
          <div className="text-[11px] text-slate-400">Sube la Constancia de Situación Fiscal (SAT) o el Acta Constitutiva en PDF y la IA llenará los campos automáticamente.</div>
        </div>
        <AIProfileReader onApply={applyAI} />
      </div>

      <div className="panel p-4">
        <div className="label-mono mb-3">// DATOS FISCALES (CONSTANCIA SAT)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {field("Razón social", "razon_social")}
          {field("RFC", "rfc")}
          {field("Régimen fiscal", "regimen_fiscal")}
          {field("Régimen de capital", "regimen_capital")}
          {field("Nombre comercial", "nombre_comercial")}
          {field("idCIF", "id_cif")}
          {field("Estatus en el padrón", "estatus_padron")}
          {field("Fecha de inicio de operaciones", "fecha_inicio_operaciones", "date")}
        </div>
        <div className="label-mono mt-4 mb-2">// DOMICILIO FISCAL</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {field("Domicilio fiscal (línea completa)", "domicilio_fiscal")}
          {field("Código postal", "codigo_postal")}
          {field("Colonia", "colonia")}
          {field("Municipio", "municipio")}
          {field("Entidad federativa", "entidad_federativa")}
        </div>
      </div>

      <div className="panel p-4">
        <div className="label-mono mb-3">// ACTA CONSTITUTIVA Y DATOS NOTARIALES</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {field("Notario", "notario")}
          {field("Notaría No.", "notaria_numero")}
          {field("Lugar de la notaría", "notaria_lugar")}
          {field("Instrumento No.", "instrumento_numero")}
          {field("Fecha de constitución", "fecha_constitucion", "date")}
          {field("Folio mercantil (Registro Público)", "folio_mercantil")}
          {field("Registro Público de la Propiedad", "registro_publico")}
          {field("Capital social", "capital_social")}
          {field("Duración de la sociedad", "duracion_sociedad")}
          {field("Tipo de administración", "tipo_administracion")}
        </div>
        <div className="mt-3">
          <label className="label-mono block mb-1">Objeto social (resumen)</label>
          <textarea data-testid="legal-profile-objeto_social" value={form.objeto_social || ""} rows={3}
            onChange={(e) => set("objeto_social", e.target.value)} className="w-full input-cyber" />
        </div>
      </div>

      <div className="panel p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="label-mono">// SOCIOS / ACCIONISTAS</div>
          <button data-testid="legal-add-socio-btn" onClick={() => set("socios", [...(form.socios || []), { ...EMPTY_SOCIO }])}
            className="btn-ghost px-2 py-1 text-xs flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Agregar socio</button>
        </div>
        {(form.socios || []).length === 0 && <div className="text-xs text-slate-500 italic">Sin socios registrados.</div>}
        <div className="space-y-2">
          {(form.socios || []).map((s, i) => (
            <div key={i} className="grid grid-cols-2 md:grid-cols-5 gap-2 items-center">
              <input placeholder="Nombre" value={s.nombre || ""} onChange={(e) => setRow("socios", i, "nombre", e.target.value)} className="input-cyber" data-testid={`legal-socio-nombre-${i}`} />
              <input placeholder="Acciones" value={s.acciones || ""} onChange={(e) => setRow("socios", i, "acciones", e.target.value)} className="input-cyber" />
              <input placeholder="Monto" value={s.monto || ""} onChange={(e) => setRow("socios", i, "monto", e.target.value)} className="input-cyber" />
              <input placeholder="%" value={s.porcentaje || ""} onChange={(e) => setRow("socios", i, "porcentaje", e.target.value)} className="input-cyber" />
              <button onClick={() => delRow("socios", i)} className="text-slate-500 hover:text-red-400 justify-self-start"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="panel p-4">
        <div className="label-mono mb-3">// ACTIVIDADES ECONÓMICAS (SAT)</div>
        {(form.actividades_economicas || []).length === 0 && <div className="text-xs text-slate-500 italic">Sin actividades registradas — usa el lector IA con la Constancia de Situación Fiscal.</div>}
        <div className="space-y-2">
          {(form.actividades_economicas || []).map((a, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_90px_130px_32px] gap-2 items-center">
              <input placeholder="Actividad" value={a.actividad || ""} onChange={(e) => setRow("actividades_economicas", i, "actividad", e.target.value)} className="input-cyber" data-testid={`legal-actividad-${i}`} />
              <input placeholder="%" value={a.porcentaje || ""} onChange={(e) => setRow("actividades_economicas", i, "porcentaje", e.target.value)} className="input-cyber" />
              <input placeholder="Fecha inicio" value={a.fecha_inicio || ""} onChange={(e) => setRow("actividades_economicas", i, "fecha_inicio", e.target.value)} className="input-cyber" />
              <button onClick={() => delRow("actividades_economicas", i)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="panel p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="label-mono">// DATOS BANCARIOS AUTORIZADOS</div>
          <button data-testid="legal-add-bank-btn" onClick={() => set("bank_accounts", [...(form.bank_accounts || []), { ...EMPTY_BANK }])}
            className="btn-ghost px-2 py-1 text-xs flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Agregar cuenta</button>
        </div>
        {(form.bank_accounts || []).length === 0 && <div className="text-xs text-slate-500 italic">Sin cuentas registradas.</div>}
        <div className="space-y-2">
          {(form.bank_accounts || []).map((b, i) => (
            <div key={i} className="grid grid-cols-2 md:grid-cols-5 gap-2 items-center">
              <input placeholder="Banco" value={b.banco} onChange={(e) => setRow("bank_accounts", i, "banco", e.target.value)} className="input-cyber" data-testid={`legal-bank-banco-${i}`} />
              <input placeholder="CLABE" value={b.clabe} onChange={(e) => setRow("bank_accounts", i, "clabe", e.target.value)} className="input-cyber" />
              <input placeholder="Cuenta" value={b.cuenta} onChange={(e) => setRow("bank_accounts", i, "cuenta", e.target.value)} className="input-cyber" />
              <input placeholder="Titular" value={b.titular} onChange={(e) => setRow("bank_accounts", i, "titular", e.target.value)} className="input-cyber" />
              <button onClick={() => delRow("bank_accounts", i)} className="text-slate-500 hover:text-red-400 justify-self-start"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="panel p-4">
        <div className="label-mono mb-2">// NOTAS</div>
        <textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={3}
          className="w-full input-cyber" data-testid="legal-profile-notes" />
      </div>
      <button data-testid="legal-profile-save-btn" onClick={save} disabled={saving}
        className="btn-primary px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar expediente
      </button>
    </div>
  );
}
